import { AlertTriangle, CheckCircle2, CircleSlash } from 'lucide-react';
import clsx from 'clsx';
import type { DeadlineVerdict, DispatchPreview } from '../dispatch.api';
import { deadlineText, type DeadlineTone } from '../policies/plan-deadline-text.policy';

const arrivalFormat = new Intl.DateTimeFormat('en-KE', { dateStyle: 'medium', timeStyle: 'short' });

const TONE_ICON = { ok: CheckCircle2, risk: AlertTriangle, neutral: CircleSlash } as const;
const TONE_CLASS: Record<DeadlineTone, string> = { ok: 'text-success', risk: 'text-error', neutral: 'text-muted' };

function DeadlineRow({ term, name, verdict }: { term: string; name: string; verdict: DeadlineVerdict }) {
  const text = deadlineText(verdict, name);
  const Icon = TONE_ICON[text.tone];
  const arrival = 'arrivalAt' in verdict ? arrivalFormat.format(new Date(verdict.arrivalAt)) : null;
  return (
    <div className="contents">
      <dt className="text-muted">{term}</dt>
      <dd className="text-fg">
        <span className={clsx('flex items-center gap-2 font-medium', TONE_CLASS[text.tone])}>
          <Icon className="size-4" aria-hidden /> {text.headline}
        </span>
        {text.detail && <span className="block text-muted">{text.detail}</span>}
        {arrival && <span className="block text-muted">Expected arrival {arrival}.</span>}
      </dd>
    </div>
  );
}

/** Whether the plan's route, with rest added, reaches the job's deadline: the one that applies to this trip's leg (gitaumoses4/chukua#410). */
export function DispatchPlanCard({ preview }: { preview: DispatchPreview }) {
  const { plan, planDeadlines } = preview;
  if (preview.dispatch) return null;
  return (
    <section aria-label="Plan and deadlines" className="rounded-lg border border-line bg-surface p-5">
      <h2 className="mb-3 text-base font-semibold text-fg">Plan and deadlines</h2>
      {!plan || !planDeadlines ? (
        <p className="text-sm text-muted">This trip has no plan yet, so there is no arrival to judge against the deadline.</p>
      ) : (
        <dl className="grid grid-cols-[10rem_1fr] gap-y-3 text-sm">
          {planDeadlines.delivery.status !== 'not_applicable' && (
            <DeadlineRow term="Delivery deadline" name="delivery deadline" verdict={planDeadlines.delivery} />
          )}
          {planDeadlines.emptyReturn.status !== 'not_applicable' && (
            <DeadlineRow term="Empty-return deadline" name="empty-return deadline" verdict={planDeadlines.emptyReturn} />
          )}
          {planDeadlines.delivery.status === 'not_applicable' && planDeadlines.emptyReturn.status === 'not_applicable' && (
            <p className="col-span-2 text-muted">This kind of trip has no deadline to judge.</p>
          )}
        </dl>
      )}
    </section>
  );
}
